import {Component, OnInit, OnDestroy, inject} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Observable, Subscription} from 'rxjs';
import {
  BreakpointsService,
  MediaQueryResultMap,
  BIPPERZ_BREAKPOINTS,
} from '../../../../shared/services/breakpoint.service';
import {IJobOffer, ButtonSize, ButtonState, ButtonType} from '../../../../../assets/types/dtoTypes';
import {Location, NgClass, NgForOf, NgIf} from '@angular/common';
import {JobDescriptionService} from './jobs-description.service';
import {ImageService} from '../../../../shared/services/image.service';
import {AuthenticationService} from '../../../../shared/services/authentication.service';
import {ShowPopupService} from '../../../../shared/services/show-popup.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {PATH} from '../../../../../assets/constants';
import {CompanyDashboardPageService} from '../../company/company-dashboard-page/company-dashboard-page.service';
import {MatDialog} from '@angular/material/dialog';
import {JobApplyModalComponent} from '../../../../modals/job-apply-modal/job-apply-modal.component';
import {ButtonComponent} from "../../../../shared/button/button.component";
import {ExternalHeaderComponent} from "../../../../shared/external-header/external-header.component";
import {LoadMaskComponent} from "../../../../shared/load-mask/load-mask.component";
import {FooterComponent} from "../../../../shared/footer/footer.component";

@Component({
  selector: 'app-jobs-description',
  templateUrl: './jobs-description.component.html',
  styleUrls: ['./jobs-description.component.scss'],
  imports: [
    ButtonComponent,
    ExternalHeaderComponent,
    TranslateModule,
    NgIf,
    NgForOf,
    LoadMaskComponent,
    FooterComponent,
    NgClass
  ],
  standalone: true
})
export class JobDescriptionComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private jobDecriptionService = inject(JobDescriptionService);
  private translate = inject(TranslateService);
  private imageService = inject(ImageService);
  private dialog = inject(MatDialog);
  private popupService = inject(ShowPopupService);
  private authService = inject(AuthenticationService);
  private companyService = inject(CompanyDashboardPageService);
  private breakpointsService = inject(BreakpointsService);
  private location = inject(Location);

  isLoading = false;

  protected readonly ButtonSize = ButtonSize;

  protected readonly ButtonState = ButtonState;

  protected readonly ButtonType = ButtonType;

  job?: IJobOffer;

  userId: string;

  isMobile = false;

  description: string;

  breakpoints$: Observable<MediaQueryResultMap>;

  private bpSub?: Subscription;

  constructor() {
    this.breakpoints$ = this.breakpointsService.observe([
      BIPPERZ_BREAKPOINTS.s,
      BIPPERZ_BREAKPOINTS.m_down,
    ]);
  }


  ngOnInit(): void {
    this.userId = this.authService.getIdFromAccessToken();
    this.bpSub = this.breakpoints$.subscribe(result => {
      this.isMobile = result.s || result.m_down;
    });
    const jobId = this.route.snapshot.paramMap.get('jobId');
    if (jobId) {
      this.jobDecriptionService.getJobOffer(jobId).subscribe(job => {
        this.job = job;
        this.companyService.getCompanyData(this.job.companyId).subscribe({
          next: (data) => {
            this.description = data.description;
          },
        });
        this.imageService.appendSasTokenToCompanyImage(this.job.companyLogoUrl, this.job.companyId, true).subscribe((url) => {
          this.job.companyLogoUrl = url;
        });
      });

    }
  }

  goBack(): void {
    this.location.back();
  }

  ngOnDestroy(): void {
    this.bpSub?.unsubscribe();
  }

  applyForJob(): void {
    const dialogRef = this.dialog.open(JobApplyModalComponent, {
      width: '500px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.jobDecriptionService.createApplication(
          this.userId,
          this.job.id,
          result.message,
          result.includeCV,
        ).subscribe({
          next: () => {
            this.isLoading = false;
            this.popupService.showPopup({
              status: 'isSuccess',
              translationKey: 'job_details.popup.application.success',
            });
            this.router.navigate([`${PATH.MAIN}/${this.userId}`]);
          },
          error: () => {
            this.isLoading = false;
            this.popupService.showPopup({
              status: 'failure',
              translationKey: 'job_details.popup.application.error',
            });
          },
        });
      }
    });
  }


  getTimeAgo(dateString: string): string {
    const now = new Date();
    const createdDate = new Date(dateString);
    createdDate.setHours(createdDate.getHours() + 2);
    const diffInSeconds = Math.floor((now.getTime() - createdDate.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return this.translate.instant('timeAgo.justNow');
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      const key = diffInMinutes === 1 ? 'timeAgo.minutesAgo' : 'timeAgo.minutesAgo_plural';
      return this.translate.instant(key, { count: diffInMinutes });
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      const key = diffInHours === 1 ? 'timeAgo.hoursAgo' : 'timeAgo.hoursAgo_plural';
      return this.translate.instant(key, { count: diffInHours });
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      const key = diffInDays === 1 ? 'timeAgo.daysAgo' : 'timeAgo.daysAgo_plural';
      return this.translate.instant(key, { count: diffInDays });
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      const key = diffInWeeks === 1 ? 'timeAgo.weeksAgo' : 'timeAgo.weeksAgo_plural';
      return this.translate.instant(key, { count: diffInWeeks });
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      const key = diffInMonths === 1 ? 'timeAgo.monthsAgo' : 'timeAgo.monthsAgo_plural';
      return this.translate.instant(key, { count: diffInMonths });
    }

    const diffInYears = Math.floor(diffInDays / 365);
    const key = diffInYears === 1 ? 'timeAgo.yearsAgo' : 'timeAgo.yearsAgo_plural';
    return this.translate.instant(key, { count: diffInYears });
  }

}
